import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, ApiError } from "./client";

export interface AdminUser {
  id: string;
  email: string;
  fullName: string | null;
  childCount: number;
  isPlatformAdmin: boolean;
  disabledAt: string | null;
  termsAcceptedAt: string | null;
  mfaEnabled: boolean;
  createdAt: string;
}

export interface AdminUsersPage {
  users: AdminUser[];
  total: number;
  active: number;
}

export interface AdminFeedback {
  id: string;
  name: string | null;
  email: string | null;
  rating: number | null;
  feedback: string;
  /** The page a signed-in tester sent it from (tokens and ids masked); null from the landing form. */
  pagePath: string | null;
  createdAt: string;
}

const adminUsersKey = (search: string) => ["admin", "users", search] as const;
const ADMIN_FEEDBACK_KEY = ["admin", "feedback"] as const;

export function useAdminUsers(search: string) {
  return useQuery<AdminUsersPage, ApiError>({
    queryKey: adminUsersKey(search),
    queryFn: () =>
      apiClient<AdminUsersPage>(
        `/api/admin/users${search ? `?search=${encodeURIComponent(search)}` : ""}`,
      ),
  });
}

export function useAdminFeedback() {
  return useQuery<AdminFeedback[], ApiError>({
    queryKey: ADMIN_FEEDBACK_KEY,
    queryFn: () => apiClient<AdminFeedback[]>("/api/admin/feedback"),
  });
}

export function useSetUserDisabled() {
  const queryClient = useQueryClient();
  return useMutation<AdminUser, ApiError, { id: string; disabled: boolean }>({
    mutationFn: ({ id, disabled }) =>
      apiClient<AdminUser>(`/api/admin/users/${id}/disabled`, {
        method: "PATCH",
        body: JSON.stringify({ disabled }),
      }),
    // Refetch rather than patch the cache: the row carries derived counts, and
    // a suspension is rare enough that a round trip is the honest option.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}
